import { ChangeDetectionStrategy, Component, computed, inject, input, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { TranslatePipe } from '@ngx-translate/core';
import { IconUIComponent } from '../icon/icon.component';
import { DeviceOrientationType } from '../../../types/rotate-device.type';

const PHONE_QUERY = Breakpoints.Handset;
const LANDSCAPE_QUERY = '(orientation: landscape)';
// Keeps the overlay off desktop browsers narrowed down to a handset-sized window.
const TOUCH_QUERY = '(pointer: coarse)';

@Component({
  selector: 'fr8bot-ui-rotate-device',
  imports: [IconUIComponent, TranslatePipe],
  templateUrl: './rotate-device.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'tw:fixed tw:inset-0 tw:z-[1000] tw:backdrop-blur-[2px]',
    '[style.display]': "show() ? 'block' : 'none'",
  },
})
export class RotateDeviceUIComponent {
  readonly allowedOrientation = input<DeviceOrientationType>('portrait');

  private readonly breakpointObserver = inject(BreakpointObserver);

  private readonly isPhone = this.matches(PHONE_QUERY);
  private readonly isTouch = this.matches(TOUCH_QUERY);
  private readonly isLandscape = this.matches(LANDSCAPE_QUERY);

  readonly show = computed(() => {
    if (!this.isPhone() || !this.isTouch()) return false;

    const current: DeviceOrientationType = this.isLandscape() ? 'landscape' : 'portrait';
    return current !== this.allowedOrientation();
  });

  /**
   * `observe()` splits a comma-separated query such as `Breakpoints.Handset` into its parts, so the
   * combined string is never a key of `state.breakpoints` — only the OR-ed `matches` flag is usable.
   */
  private matches(query: string): Signal<boolean> {
    return toSignal(this.breakpointObserver.observe(query).pipe(map(({ matches }) => matches)), {
      initialValue: false,
    });
  }
}
